const KeenSlider = window.KeenSlider
const VISIBLE_SLIDE_PORTION_THRESHOLD = 0.5

const { setInitialised, useA11y, useArrows } = window.BAO.Carousel.plugins

class CartCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.carousel = this.setupCarousel()
    this.setupListeners()
    this.updateSlideAccessibility()
    this.setupSlideBoundaryFocusManagement()
  }

  setupListeners () {
    if (!this.carousel) return

    this.carousel.on('slideChanged', () => {
      this.updateSlideAccessibility()
    })

    this.carousel.on('animationEnded', () => {
      this.updateSlideAccessibility()
    })
  }

  updateSlideAccessibility () {
    if (!this.els.slide?.exists || !this.carousel?.track?.details) return

    const slides = this.els.slide.elements
    const slideDetails = this.carousel.track.details.slides || []

    slides.forEach((slide, index) => {
      const isVisible =
        (slideDetails[index]?.portion || 0) >= VISIBLE_SLIDE_PORTION_THRESHOLD

      slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true')

      const focusableSelector =
        'button, [href], input, select, textarea, [tabindex]'
      const focusableElements = slide.querySelectorAll(focusableSelector)

      focusableElements.forEach((el) => {
        if (isVisible) {
          const originalTabIndex = el.getAttribute(
            'data-cart-carousel-original-tabindex',
          )

          if (originalTabIndex === null) {
            el.removeAttribute('tabindex')
          } else {
            el.setAttribute('tabindex', originalTabIndex)
          }

          el.removeAttribute('data-cart-carousel-original-tabindex')
        } else {
          if (!el.hasAttribute('data-cart-carousel-original-tabindex')) {
            const tabIndex = el.getAttribute('tabindex')

            el.setAttribute(
              'data-cart-carousel-original-tabindex',
              tabIndex === null ? '' : tabIndex,
            )
          }

          el.setAttribute('tabindex', '-1')
        }
      })
    })
  }

  setupSlideBoundaryFocusManagement () {
    if (!this.els.slides?.element) return

    this.listeners.add(this.els.slides.element, 'keydown', (event) => {
      if (event.key !== 'Tab') return

      const focusedSlide = event.target.closest(this.els.slide.selector)

      if (!focusedSlide) return

      const slideFocusables = this.getFocusableElements(focusedSlide)

      if (!slideFocusables.length) return

      const boundaryFocusable = event.shiftKey
        ? slideFocusables[0]
        : slideFocusables[slideFocusables.length - 1]

      if (event.target !== boundaryFocusable) return

      const visibleSlides = this.getVisibleSlides()

      if (!visibleSlides.length) return

      const boundarySlide = event.shiftKey
        ? visibleSlides[0]
        : visibleSlides[visibleSlides.length - 1]

      if (focusedSlide !== boundarySlide) return

      const targetArrow = event.shiftKey
        ? this.els?.previous?.element
        : this.els?.next?.element

      if (!targetArrow || targetArrow.disabled) return

      event.preventDefault()
      targetArrow.focus({ preventScroll: true })
    })
  }

  getVisibleSlides () {
    if (!this.els.slide?.exists || !this.carousel?.track?.details) return []

    const slideDetails = this.carousel.track.details.slides || []

    return this.els.slide.elements.filter((slide, index) => {
      return (
        (slideDetails[index]?.portion || 0) >= VISIBLE_SLIDE_PORTION_THRESHOLD
      )
    })
  }

  getFocusableElements (container) {
    if (!container) return []

    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

    return Array.from(container.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hasAttribute('disabled'),
    )
  }

  setupCarousel () {
    return new KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        defaultAnimation: {
          duration: 1500,
        },
        loop: false,
        mode: 'snap',
        slides: {
          perView: 1.9,
          spacing: 10,
        },
      },
      [
        setInitialised(),
        useA11y({
          includeTabindex: false,
          includeKeyboardControls: false,
          // updateSlideAccessibility() below is the single owner of
          // aria-hidden/tabindex state for slides; letting useA11y also
          // manage slide focus would let it cache its own temporary
          // tabindex="-1" as the "original" value and restore that stale
          // value once a slide becomes visible again.
          manageSlideFocus: false,
        }),
        useArrows({
          next: this.els?.next?.element,
          previous: this.els?.previous?.element,
        }),
      ],
    )
  }
}

if (!customElements.get('cart-carousel')) {
  customElements.define('cart-carousel', CartCarousel)
}
